<template>
    <div v-if="discountForEdit">
        <v-form ref="form" v-model="isValidGeneral" :disabled="isSaveLoading" class="pb-4">
            <DiscountGeneralInformation
                v-model="discountForEdit"
                :selected-locale="selectedLocale"
                :discount-type-items="discountTypeItems"
            >
                <template v-if="contract?.serviceType !== 'ownHotel' && contract?.serviceType !== 'ownCarRent'">
                    <div class="pt-8">
                        <div class="font-weight-bold text-subtitle-2 pb-2">
                            {{ $t('discount_equal_to') + ' *' }}
                        </div>
                        <DiscountAmount v-model="discountForEdit" />
                    </div>
                </template>
            </DiscountGeneralInformation>
            <DiscountPeriod
                ref="discountPeriodTariffsRef"
                v-model="discountForEdit"
                :is-tariffs-loading="isTariffsLoading"
                :tariffs="tariffs"
            />
            <DiscountTermsOfUse v-if="discountForEdit?.discountTerms" v-model="discountForEdit.discountTerms" />
            <DiscountOnLists v-model="discountForEdit" :service-type="contract?.serviceType" />
            <template
                v-if="
                    contract?.serviceType !== 'ownHotel' &&
                    contract?.serviceType !== 'ownCarRent' &&
                    discountForEdit?.discountConditions
                "
            >
                <DiscountRestrictions v-model="discountForEdit.discountConditions" />
            </template>
            <template v-if="contract?.serviceType === 'ownHotel' || contract?.serviceType === 'ownCarRent'">
                <DiscountConditions v-model="discountForEdit" :service-type="contract?.serviceType" />
            </template>
            <template v-if="contract?.serviceType === 'ownHotel'">
                <DiscountCalculation v-model="discountForEdit.calculationType" />
            </template>
        </v-form>
        <v-row no-gutters class="gc-4">
            <v-btn :loading="isSaveLoading" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="close" variant="text" @click="emit('onClose')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </div>
</template>

<script setup lang="ts">
    import {clone} from '~/utils/helpers'
    import {contractServiceType} from '~/utils/types/types'

    const props = defineProps<{
        contract: any
        discount: any
        selectedLocale: string
    }>()

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    const {membershipLevels} = useMembershipLevels()
    const deviceTypeItem = useDeviceTypeItems.value
    const allocationTypeItems = useAllocationTypeItems.value

    const isValidGeneral = ref<boolean>(false)
    const discountForEdit = ref<any>(null)
    const isSaveLoading = ref<boolean>(false)
    const isTariffsLoading = ref<boolean>(false)
    const tariffs = ref<any[]>([])
    const editMode = ref<boolean>(false)
    const discountPeriodTariffsRef = ref(null)
    const form = ref(null)

    const emit = defineEmits<{
        (e: 'onClose' | 'onSave'): void
    }>()

    onMounted(async () => {
        await loadTariffs()
        await initValues()

        if (editMode.value === false) {
            if (!discountForEdit.value.tariffLinks.length && tariffs.value.length) {
                discountPeriodTariffsRef.value.addTariffLink()
            }
        }
    })

    const initValues = async () => {
        if (props.discount) {
            discountForEdit.value = clone(props.discount)
        } else {
            discountForEdit.value = {
                contractId: props.contract.id,
                name: {},
                // ZSH: always EXTERNAL (SPO) since the radio group is hidden
                type: 'EXTERNAL',
                forAllServices: true,
                servicesAvailability: [],
                forAllSecondaryServices: true,
                secondaryServicesAvailability: [],
                forAllPaxes: true,
                paxesAvailability: [],
                forAllAllocations: true,
                allocationsAvailability: [],
                forAllMemberships: true,
                membershipLevels: [],
                forAllCitizenships: true,
                limitsOnCitizenship: [],
                forAllDeviceTypes: true,
                deviceTypes: [],
                amount: null,
                discountConditions: {
                    nature:
                        props.contract?.serviceType === contractServiceType.ownHotel ||
                        props.contract?.serviceType === contractServiceType.ownCarRent
                            ? 'ON_EXACT_NIGHT'
                            : 'FROM_TOTAL',
                    groupNature: 'FOR_ALL',
                    discountValue: null,
                    oneTimeDiscount: true,
                    unitOfMeasurement: 'PERCENT',
                    freeNightsStartingCondition: 'FROM_EXACT_NIGHT',
                },
                discountTerms: {},
                active: true,
                tariffLinks: [],
            }
            if (props.contract?.serviceType === contractServiceType.ownHotel) {
                discountForEdit.value.calculationType = 'MEAL_PLACE'
            }
            if (
                props.contract?.serviceType === contractServiceType.ownHotel ||
                props.contract?.serviceType === contractServiceType.ownCarRent
            ) {
                discountForEdit.value.discountConditions.applicableNight = 1
            }
        }

        // ZSH: force EXTERNAL (SPO) for existing discounts too, since type selector is hidden
        discountForEdit.value.type = 'EXTERNAL'

        editMode.value = !!discountForEdit.value.id

        if (!discountForEdit.value.discountTerms) {
            discountForEdit.value.discountTerms = {
                daysPrior: null,
                bookingDateFrom: null,
                bookingDateTo: null,
            }
        }

        if (discountForEdit.value.forAllMemberships) {
            discountForEdit.value.membershipLevels = membershipLevels.value.map(({code}) => code)
        }
        if (discountForEdit.value.forAllCitizenships) {
            discountForEdit.value.limitsOnCitizenship = useCountries.value.map(el => el.id)
        }
        if (discountForEdit.value.forAllDeviceTypes) {
            discountForEdit.value.deviceTypes = deviceTypeItem?.map(({id}) => id)
        }

        if (props.contract?.serviceType === contractServiceType.ownHotel) {
            if (!supplierStore.paxes?.length) await supplierStore.loadPaxes()
            if (!supplierStore.mealTypes?.length) await supplierStore.loadHotelSecondaryServices()
            if (!supplierStore.productServices?.length) await supplierStore.loadProductServices()

            if (discountForEdit.value.forAllAllocations) {
                discountForEdit.value.allocationsAvailability = allocationTypeItems.map(({id}) => id)
            }
            if (discountForEdit.value.forAllPaxes) {
                discountForEdit.value.paxesAvailability = usePaxes.value.map(({id}) => id)
            }
            if (discountForEdit.value.forAllSecondaryServices) {
                discountForEdit.value.secondaryServicesAvailability = useMealTypes.value.map(({id}) => id)
            }
            if (discountForEdit.value.forAllServices) {
                discountForEdit.value.servicesAvailability = useRoomTypes.value.map(({id}) => id)
            }
        }
    }

    const loadTariffs = async () => {
        try {
            isTariffsLoading.value = true
            tariffs.value = (await $api.tariffs.get({contractId: props.contract.id, active: true})).tariffs
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isTariffsLoading.value = false
        }
    }

    const clear = () => {
        discountForEdit.value = null
        tariffs.value = []
    }

    const save = async () => {
        await form.value.validate()
        if (!isValidGeneral.value) return

        const discountForEditAdapted = clone(discountForEdit.value)

        discountForEditAdapted.forAllCitizenships =
            discountForEdit.value.limitsOnCitizenship.length === useCountries.value.length
        if (discountForEditAdapted.forAllCitizenships) {
            discountForEditAdapted.limitsOnCitizenship = []
        }

        discountForEditAdapted.forAllMemberships =
            discountForEdit.value.membershipLevels.length === membershipLevels.value.length
        if (discountForEditAdapted.forAllMemberships) {
            discountForEditAdapted.membershipLevels = []
        }

        discountForEditAdapted.forAllDeviceTypes = discountForEdit.value.deviceTypes.length === deviceTypeItem.length
        if (discountForEditAdapted.forAllDeviceTypes) {
            discountForEditAdapted.deviceTypes = []
        }

        if (props.contract?.serviceType === contractServiceType.ownHotel) {
            discountForEditAdapted.forAllAllocations =
                discountForEdit.value.allocationsAvailability.length === allocationTypeItems.length
            if (discountForEditAdapted.forAllAllocations) {
                discountForEditAdapted.allocationsAvailability = []
            }

            discountForEditAdapted.forAllPaxes =
                discountForEdit.value.paxesAvailability.length === usePaxes.value.length
            if (discountForEditAdapted.forAllPaxes) {
                discountForEditAdapted.paxesAvailability = []
            }

            discountForEditAdapted.forAllSecondaryServices =
                discountForEdit.value.secondaryServicesAvailability.length === useMealTypes.value.length
            if (discountForEditAdapted.forAllSecondaryServices) {
                discountForEditAdapted.secondaryServicesAvailability = []
            }

            discountForEditAdapted.forAllServices =
                discountForEdit.value.servicesAvailability.length === useRoomTypes.value.length
            if (discountForEditAdapted.forAllServices) {
                discountForEditAdapted.servicesAvailability = []
            }
        }

        if (editMode.value) {
            await saveEdit(discountForEditAdapted)
        } else {
            await saveCreate(discountForEditAdapted)
        }
    }

    const saveEdit = async discountForEditAdapted => {
        try {
            isSaveLoading.value = true
            await $api.ownProductDiscounts.put(discountForEditAdapted)

            $toast.success(t('success_message.saved_successfully'))
            clear()
            emit('onClose')
            emit('onSave')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || $t('error_message.save_error'))
        } finally {
            isSaveLoading.value = false
        }
    }

    const saveCreate = async discountForEditAdapted => {
        try {
            isSaveLoading.value = true
            await $api.ownProductDiscounts.post(discountForEditAdapted)

            $toast.success(t('success_message.saved_successfully'))
            clear()
            emit('onClose')
            emit('onSave')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || $t('error_message.save_error'))
        } finally {
            isSaveLoading.value = false
        }
    }

    const discountTypeItems = computed(() => {
        let discounts = []
        switch (props.contract?.serviceType) {
            case contractServiceType.ownHotel:
                discounts = ['HIDDEN', 'EXTERNAL', 'HOT_DEAL', 'PERCENT', 'SALE', 'FREE_NIGHTS']
                break
            case contractServiceType.ownCarRent:
                discounts = ['EXTERNAL', 'HOT_DEAL', 'PERCENT', 'SALE', 'FREE_NIGHTS']
                break
            case contractServiceType.ownCruise:
                discounts = ['HOT_DEAL']
                break
            default:
                discounts = ['EXTERNAL', 'HOT_DEAL', 'PERCENT', 'SALE']
        }

        return discounts.map(el => ({
            title: t(`discountType.${el}`),
            value: el,
        }))
    })

    const useCountries = computed(() => {
        return runtimeStore.countries
    })
    const useMealTypes = computed(() => {
        return supplierStore.mealTypes.filter(({active}) => active)
    })
    const useRoomTypes = computed(() => {
        return supplierStore.productServices.filter(({active}) => active)
    })
</script>
